import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Button, Grid, Paper, Typography } from '@mui/material'
import { AccordionWrapper, CustomTabs, UserTable } from 'components'
import { CustomerStatusProvider } from 'contexts/CustomerStatusProvider'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { createUsers } from 'services/cmAPI/createUsers'
import { deleteUser } from 'services/cmAPI/deleteUser'
import { updateUser } from 'services/cmAPI/updateUser'
import { useGetSelectedCustomerData } from './SelectedCustomer.controller'
import GeneralInformation from './widgets/GeneralInformation'
import GlobalDataTable from './widgets/GlobalDataTable'
import Restrictions from './widgets/Restrictions'
import UserSettingsHandler from './widgets/UserSettingsHandler'

const SelectedCustomer = () => {
  const [planNames, setPlanNames] = useState<string[]>([])
  const { id: selectedCustomer } = useParams()
  const navigate = useNavigate()
  const { data, isLoading, isSuccess } =
    useGetSelectedCustomerData(selectedCustomer)

  const { usersData, customerData, customerPlanData, plansData } = data
  const isExpired = customerData?.expires_at < new Date().toISOString()
  useEffect(() => {
    if (isSuccess) setPlanNames(plansData?.map((planObj) => planObj.name))
  }, [plansData, isSuccess])
  const dashboardUsers = usersData?.filter((user) => user.userData !== null)
  const customerZones = useMemo(() => {
    if (!customerPlanData?.rights?.allow_zones) return []
    const zones = Object.keys(customerPlanData.rights.allow_zones).map(
      (key) => {
        const countries = customerPlanData.rights.allow_zones[key]
        return {
          name: key,
          countries,
          allocation:
            customerPlanData.rights?.['global_lookups_limit']?.[key] || 0,
          usage:
            customerPlanData.counters['number_of_global_lookups']?.[key] || 0,
        }
      },
    )
    return zones
  }, [customerPlanData])
  return (
    <Paper sx={{ height: '100%', minHeight: '100vh' }}>
      <Grid container alignItems="center">
        <Grid item>
          <Button
            onClick={() => navigate('/')}
            startIcon={<ArrowBackIcon />}
            variant="text"
            sx={{ textTransform: 'none', fontWeight: 500 }}
            aria-label={'Go back to Customers'}
          >
            Customers
          </Button>
        </Grid>
        {customerData?.name && (
          <Grid>
            <Typography>/&nbsp;&nbsp;{customerData.name}</Typography>
          </Grid>
        )}
      </Grid>

      <AccordionWrapper isLoading={isLoading} title="General" expanded={true}>
        <GeneralInformation
          planNames={planNames}
          customerData={customerData}
          customerPlanData={customerPlanData}
        />
      </AccordionWrapper>
      <CustomTabs
        items={[
          <UserTable
            isExpired={isExpired}
            key="User Table"
            customerData={customerData}
            removeUser={deleteUser}
            updateUser={updateUser}
            createNewUser={createUsers}
            users={usersData}
            maxUsers={customerPlanData?.max_users}
            showTitle={false}
          />,
          <CustomerStatusProvider
            key="Restrictions"
            id={selectedCustomer}
            users={usersData}
          >
            <Restrictions usersLength={dashboardUsers?.length} />
          </CustomerStatusProvider>,
          <UserSettingsHandler
            customerData={customerData}
            key="User Settings"
          />,
          <GlobalDataTable
            key="Global Zone Subscriptions"
            zones={customerZones}
            showTitle={false}
          />,
        ]}
        tabs={[
          {
            label: `Users (${dashboardUsers?.length})`,
            index: 0,
          },
          {
            label: 'Restrictions',
            index: 1,
          },
          {
            label: 'User Settings',
            index: 2,
          },
          {
            label: 'Global Data',
            index: 3,
            disabled: !customerPlanData?.rights?.global?.global_data,
          },
        ]}
        isLoading={isLoading}
      />
    </Paper>
  )
}

export default SelectedCustomer
